import type { ReactElement, ReactNode } from 'react';
import { genreName, useGamesSite } from '../context';
import { Breadcrumbs } from '../components/PublicSiteFrame';
import { GameIcon } from '../components/GameIcon';
import { GameShelf } from '../components/GameShelf';
import { PlayButton } from '../components/PlayButton';
import { formatGameDate, paragraphs } from '../format';
import { GAMES_HOME_PATH, gamesArchePath, gamesGenrePath } from '../routes';
import type { GameDetailData } from '../types';

/**
 * One game's page: what it is, Play, and what else to play. `extra` goes
 * under Play — the app's own additions (a Home Screen hint on an iPhone).
 */
export function GameDetailBody({
  data,
  extra,
}: {
  data: GameDetailData;
  extra?: ReactNode;
}): ReactElement {
  const { Link, signedIn, genreLabels } = useGamesSite();
  const { game, moreFromArche, related } = data;
  const firstGenre = game.genres[0];
  const about = paragraphs(game.about || game.description);
  return (
    <div className="fg-wrap">
      <Breadcrumbs
        trail={[
          { label: 'Games', href: GAMES_HOME_PATH },
          ...(firstGenre
            ? [{ label: genreName(genreLabels, firstGenre), href: gamesGenrePath(firstGenre) }]
            : []),
          { label: game.title },
        ]}
      />
      <header className="fg-detail">
        <GameIcon game={game} size="xl" />
        <div className="fg-detail__copy">
          <h1 className="fg-detail__title">{game.title}</h1>
          <p className="fg-detail__sub">
            {game.kind === 'listing' ? `In ${game.archeName}` : game.description}
          </p>
          <div className="fg-detail__actions">
            <PlayButton game={game} size="large" />
            {game.requiresSignIn && !signedIn && (
              <p className="fg-detail__note">Free to join. You come straight back to the game.</p>
            )}
          </div>
          {extra}
        </div>
      </header>

      <dl className="fg-facts">
        <div>
          <dt>{game.genres.length > 1 ? 'Genres' : 'Genre'}</dt>
          <dd>
            {game.genres.length === 0
              ? 'Game'
              : game.genres.map((g, i) => (
                  <span key={g}>
                    {i > 0 && ', '}
                    <Link href={gamesGenrePath(g)}>{genreName(genreLabels, g)}</Link>
                  </span>
                ))}
          </dd>
        </div>
        {game.kind === 'listing' && (
          <div>
            <dt>Part of</dt>
            <dd>{game.archeName}</dd>
          </div>
        )}
        <div>
          <dt>On Fias since</dt>
          <dd>
            <time dateTime={game.liveAt}>{formatGameDate(game.liveAt)}</time>
          </dd>
        </div>
        <div>
          <dt>Plays in</dt>
          <dd>Your web browser</dd>
        </div>
      </dl>

      <section className="fg-about" aria-labelledby="fg-about-title">
        <h2 id="fg-about-title" className="fg-h2">
          About {game.title}
        </h2>
        {about.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </section>

      <GameShelf
        id="more"
        title={`More in ${game.archeName}`}
        games={moreFromArche}
        seeAllHref={gamesArchePath(game.archeKey)}
        seeAllLabel={`See all games in ${game.archeName}`}
        layout="tiles"
      />
      <GameShelf id="related" title="You might also like" games={related} layout="tiles" />
    </div>
  );
}

/** An address that is not (or no longer) a game here. */
export function GameNotFoundBody(): ReactElement {
  const { Link } = useGamesSite();
  return (
    <div className="fg-wrap">
      <div className="fg-empty fg-empty--page">
        <h1 className="fg-empty__title">This game isn’t on the Games page.</h1>
        <p>It may have moved, or its maker took it down.</p>
        <p>
          <Link href={GAMES_HOME_PATH} className="fg-play fg-play--large">
            Browse games
          </Link>
        </p>
      </div>
    </div>
  );
}
